import { Box, Grid } from '@mui/material';
import React from 'react';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
} from 'spw-constants';
import { useSelector } from 'react-redux';
import {
  BenchmarkReportCardType,
  BenchmarkReportIconType,
  BenchmarkReportWorkSeatRatios,
} from 'spw-redux/modules/benchmark-reports/types';
import {
  selectCurrentBenchmarkReportCategoriesMap,
  selectCurrentBenchmarkReportIconsMap,
} from 'spw-redux/modules/benchmark-reports/selectors';
import { numberWithCommas } from 'spw-shared/utils';
import { colors } from 'spw-theme';
import styled from 'styled-components';

const RatioNumber = styled.div`
  color: ${colors.black};
  font-size: 32px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin-bottom: 8px;
`;

const Colon = styled(Box)`
  text-align: center;
  font-size: 72px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
`;

const SeatType = styled(Box)`
  display: flex;
  align-items: flex-end;

  .dots-container {
    display: flex;
    justify-content: flex-end;
    width: 48px;
  }

  .dot {
    height: 16px;
    width: 16px;
    margin-right: 8px;
    border-radius: 8px;
    margin-bottom: 1.5px;
  }

  .type {
    color: ${colors.fontColor};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;
  }
`;

const WorkSeatContainer = styled(Box)`
  display: flex;
  align-items: center;
  height: 100%;

  .number {
    color: ${colors.black};
    text-align: center;
    font-size: 72px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
  }
`;

type Props = {
  ratios?: BenchmarkReportWorkSeatRatios;
};

const cardType = BenchmarkReportCardType.workSeatRatios;

const WorkSeatRatios = ({ ratios }: Props) => {
  const currentReportCategoriesMap = useSelector(
    selectCurrentBenchmarkReportCategoriesMap
  );
  const currentReportIconsMap = useSelector(
    selectCurrentBenchmarkReportIconsMap
  );

  const getIcon = (iconType: BenchmarkReportIconType) =>
    `/images/space-icons/${currentReportIconsMap[cardType][iconType]}.svg`;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      data-cy="work_seat_ratios_card"
    >
      <div className="title">Work Seat Ratios</div>
      <Grid container pl="24px" spacing={6} alignItems="center" flex={1}>
        <Grid item xs={4}>
          <SeatType>
            <Box className="dots-container">
              <Box
                className="dot"
                bgcolor={
                  currentReportCategoriesMap[MEETING_SPACES_CATEGORY].color
                }
              />
            </Box>
            <Box>
              <RatioNumber data-testid="meeting_seats_ratio">
                {numberWithCommas(ratios?.meetingSeatsRatio, true, true) ||
                  '--'}
              </RatioNumber>
              <div className="type">Meeting Seats</div>
            </Box>
          </SeatType>
          <SeatType mt={2}>
            <Box className="dots-container">
              <Box
                className="dot"
                bgcolor={
                  currentReportCategoriesMap[MEETING_SPACES_CATEGORY].color
                }
              />
              <Box
                className="dot"
                bgcolor={
                  currentReportCategoriesMap[AMENITY_SPACES_CATEGORY].color
                }
              />
            </Box>
            <Box>
              <RatioNumber data-testid="shared_seats_ratio">
                {numberWithCommas(ratios?.sharedSeatsRatio, true, true) || '--'}
              </RatioNumber>
              <div className="type">Shared Seats</div>
            </Box>
          </SeatType>
          {ratios?.headcountRatio ? (
            <SeatType mt={2}>
              <Box className="dots-container" />
              <Box>
                <RatioNumber data-testid="headcount_ratio">
                  {numberWithCommas(ratios.headcountRatio, true, true) || '--'}
                </RatioNumber>
                <div className="type">Headcount</div>
              </Box>
            </SeatType>
          ) : null}
        </Grid>
        <Grid item xs={1} display="flex" alignItems="center">
          <Colon>:</Colon>
        </Grid>
        <Grid item xs={6}>
          <WorkSeatContainer>
            <div className="number">1</div>
            <img
              src={getIcon(BenchmarkReportIconType.workSeat)}
              alt="work seat"
              width={84}
            />
            <SeatType>
              <Box
                className="dot"
                bgcolor={
                  currentReportCategoriesMap[INDIVIDUAL_WORKSPACES_CATEGORY]
                    .color
                }
              />
              <div className="type">WORK SEAT</div>
            </SeatType>
          </WorkSeatContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkSeatRatios;
